// 即時朗讀（瀏覽器內建語音合成 Web Speech API）：不需錄音或上傳
// 中英混合的文字會切段，中文用國語語音、英文用英文語音
export const ttsSupported = () => typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;

export const LS_TTS_RATE = 'gsat_tts_rate';
export const LS_TTS_AUTO = 'gsat_tts_auto';        // 閃卡翻面自動朗讀
export const TTS_RATES: [number, string][] = [[0.75, '慢'], [1, '一般'], [1.25, '快']];
export const getRate = () => { const v = Number(localStorage.getItem(LS_TTS_RATE)); return v > 0 ? v : 1; };

// 切成語言片段：連續的英文（含數字、空白與標點）為英文，其餘為中文
export function splitByLang(text: string): { lang: 'en' | 'zh'; text: string }[] {
  const out: { lang: 'en' | 'zh'; text: string }[] = [];
  const re = /[A-Za-z][A-Za-z0-9'’\-\s,.;:!?()]*/g;
  let last = 0;
  text = text.replace(/\p{Extended_Pictographic}\uFE0F?/gu, '');   // 表情符號不念
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (!/[A-Za-z]{2}/.test(m[0])) continue;   // 單一字母（如選項 A.）留給國語語音，避免語音頻繁切換
    if (m.index > last) out.push({ lang: 'zh', text: text.slice(last, m.index) });
    out.push({ lang: 'en', text: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ lang: 'zh', text: text.slice(last) });
  // 去掉只有標點或空白的片段，並合併相鄰同語言
  const merged: typeof out = [];
  for (const seg of out) {
    if (!/[\p{L}\p{N}]/u.test(seg.text)) continue;
    const prev = merged[merged.length - 1];
    if (prev && prev.lang === seg.lang) prev.text += seg.text; else merged.push({ ...seg });
  }
  return merged;
}

function pickVoice(lang: 'en' | 'zh'): SpeechSynthesisVoice | undefined {
  const voices = window.speechSynthesis.getVoices();
  const prefs = lang === 'zh' ? ['zh-TW', 'zh-HK', 'zh-CN', 'zh'] : ['en-US', 'en-GB', 'en'];
  for (const p of prefs) {
    const v = voices.find(x => x.lang.replace('_', '-').startsWith(p));
    if (v) return v;
  }
  return undefined;
}

// 數學符號轉口語（中文內容才套用）：−4 → 負4、2x − 2y → 2x 減 2y、x² → x平方、10⁻⁴ → 10的負4次方、a₁ → a1
const SUP: Record<string, string> = { '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4', '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9', '⁻': '-', '⁺': '+' };
const SUB: Record<string, string> = { '₀': '0', '₁': '1', '₂': '2', '₃': '3', '₄': '4', '₅': '5', '₆': '6', '₇': '7', '₈': '8', '₉': '9' };
const OPS: [RegExp, string][] = [
  [/≤/g, ' 小於等於 '], [/≥/g, ' 大於等於 '], [/≠/g, ' 不等於 '], [/</g, ' 小於 '], [/>/g, ' 大於 '],
  [/[=＝]/g, ' 等於 '], [/[+＋]/g, ' 加 '], [/[×✕]/g, ' 乘 '], [/÷/g, ' 除以 '], [/√/g, ' 根號 '], [/π/g, ' pi '],
];
export function mathToSpeech(text: string): string {
  let t = text
    .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺]+/g, m => {
      const e = Array.from(m).map(c => SUP[c]).join('');
      return e === '2' ? '平方' : e === '3' ? '立方' : `的${e.replace('-', '負')}次方`;
    })
    .replace(/[₀-₉]/g, c => SUB[c])
    .replace(/\^\s*\(?\s*([-−]?\d+)\s*\)?/g, (_, e) => `的${e.replace(/[-−]/, '負')}次方`);
  // 減號：左右都是數字／字母／括號才念「減」；前面是開頭、括號、運算子等則念「負」；其餘（如「冊-單元」）當作停頓
  t = t.replace(/[-−–]/g, (m, i: number, str: string) => {
    let l = i - 1; while (l >= 0 && str[l] === ' ') l--;
    let r = i + 1; while (r < str.length && str[r] === ' ') r++;
    const L = l >= 0 ? str[l] : '', R = str[r] ?? '';
    const operand = (c: string) => /[0-9A-Za-z)）]/.test(c);
    if (operand(L) && /[0-9A-Za-z(（√]/.test(R)) return ' 減 ';
    if ((L === '' || /[\s(（=＝,，、:：;；<>≤≥+×÷.。\n]/.test(L) || i === 0) && /[0-9A-Za-z(（√.]/.test(R)) return '負';
    return ' ';
  });
  for (const [re, w] of OPS) t = t.replace(re, w);
  return t;
}

let current = 0;   // 朗讀序號：新的朗讀開始時，舊的回呼不再觸發

export function stopSpeaking() {
  current++;
  if (ttsSupported()) window.speechSynthesis.cancel();
}

// 朗讀文字；結束或被中斷時呼叫 onEnd
export function speak(text: string, onEnd?: () => void) {
  if (!ttsSupported()) { onEnd?.(); return; }
  stopSpeaking();
  const id = current;
  const hasZh = /[一-鿿]/.test(text) || /^[\s\d.\-−+×÷=()²³⁻]+$/.test(text);   // 中文內容或純數字式才轉口語
  const segs = splitByLang((hasZh ? mathToSpeech(text) : text).replace(/\s+/g, ' ').trim());
  if (!segs.length) { onEnd?.(); return; }
  const rate = getRate();
  segs.forEach((seg, i) => {
    const u = new SpeechSynthesisUtterance(seg.text);
    u.lang = seg.lang === 'zh' ? 'zh-TW' : 'en-US';
    const v = pickVoice(seg.lang);
    if (v) u.voice = v;
    u.rate = rate;
    if (i === segs.length - 1) {
      u.onend = () => { if (id === current) onEnd?.(); };
      u.onerror = () => { if (id === current) onEnd?.(); };
    }
    window.speechSynthesis.speak(u);
  });
}

// 部分瀏覽器的語音清單是非同步載入；先觸發一次
if (ttsSupported()) { window.speechSynthesis.getVoices(); }
